/**
 * src/lib/api/quoteCopilot.js — TICKET-872 client for the Quote Copilot.
 *
 * Reads are Supabase-native (quote_copilot_turns / quote_open_items, RLS read for
 * authenticated). The turn and the revert go through the existing `agent.chat`
 * action with `scope: 'quote_copilot'` (api/ai.js, TICKET-871) — no new endpoint.
 *
 * A read failure is returned as `error` (and logged to app_error_logs), never
 * swallowed into an empty list: an empty thread and a broken read must look
 * different on screen.
 */

import { supabase } from '../supabase';
import { apiFetch, getAuthHeaders } from './client';
import { logAppError } from '../errorReporter';

const AI = '/api/ai';
const THREAD_LIMIT = 100;

function readFailed(step, dealId, error) {
  logAppError({
    type: 'quote_copilot_fetch_error',
    message: `${step} failed: ${error.message}`,
    view: 'QuoteCopilot',
    metadata: { step, code: error.code || null, dealId },
  });
  return { rows: [], error: error.message || 'read_failed' };
}

/** The deal's thread, oldest first (the newest THREAD_LIMIT turns). */
export async function fetchCopilotTurns(dealId) {
  const { data, error } = await supabase
    .from('quote_copilot_turns')
    .select('id, role, content, tool_calls, revision_id, quote_id, created_at')
    .eq('deal_id', dealId)
    .is('archived_at', null)
    .order('created_at', { ascending: false })
    .limit(THREAD_LIMIT);
  if (error) return readFailed('fetchCopilotTurns', dealId, error);
  return { rows: (data || []).slice().reverse(), error: null };
}

/** Every live open item on the deal, oldest first. */
export async function fetchCopilotOpenItems(dealId) {
  const { data, error } = await supabase
    .from('quote_open_items')
    .select('id, question, owner, status, answer, bundle_id, line_id, source_turn_id, created_at')
    .eq('deal_id', dealId)
    .is('archived_at', null)
    .order('created_at', { ascending: true });
  if (error) return readFailed('fetchCopilotOpenItems', dealId, error);
  return { rows: data || [], error: null };
}

/**
 * One copilot turn. Returns the handler's RETURN CONTRACT spread over
 * `{ success: true }`, or `{ success:false, status, error }` (503 kill switch,
 * 429 daily turn cap, 400 bad request).
 * Up to 8 provider round-trips server-side, so the 15s default is far too short.
 */
export function sendCopilotTurn({ dealId, quoteId = null, message }) {
  return apiFetch(AI, {
    method: 'POST',
    body: JSON.stringify({
      action: 'agent.chat', scope: 'quote_copilot',
      deal_id: dealId, quote_id: quoteId || undefined, message,
    }),
    timeout: 120000,
    noRetry: true, // a 5xx after the model ran must not buy (and write) the turn twice
  });
}

/** Pure: split an SSE text buffer into complete {event, data} frames + the unfinished tail. */
export function parseSseFrames(buffer) {
  const frames = [];
  const parts = String(buffer).split(/\r?\n\r?\n/);
  const rest = parts.pop();
  for (const part of parts) {
    let event = 'message';
    const data = [];
    for (const line of part.split(/\r?\n/)) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    }
    if (!data.length) continue;
    try { frames.push({ event, data: JSON.parse(data.join('\n')) }); } catch { /* spine-emit-ok: malformed frame skipped; the final done/error event decides the outcome */ }
  }
  return { frames, rest };
}

/**
 * TICKET-912 slice 2b: one copilot turn with LIVE progress. `onProgress` gets
 * {type:'thinking'} before each model step and {type:'tool', name, ok, wrote, summary,
 * change} after each tool. `signal` (AbortController) is the Stop button: the server
 * sees the disconnect and halts the loop at its next step (what was written stays and
 * can be Reverted). Resolves to the same shape as sendCopilotTurn, or
 * {success:false, aborted:true} on Stop. Never retried: a turn is paid and writes.
 */
export async function streamCopilotTurn({ dealId, quoteId = null, message, onProgress, signal }) {
  let res;
  try {
    const auth = await getAuthHeaders();
    res = await fetch(AI, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...auth },
      body: JSON.stringify({ action: 'agent.chat', scope: 'quote_copilot', stream: true, deal_id: dealId, quote_id: quoteId || undefined, message }),
      signal,
    });
  } catch (e) {
    if (e?.name === 'AbortError') return { success: false, aborted: true }; // spine-emit-ok: the user pressed Stop
    logAppError({ type: 'quote_copilot_stream_error', message: `turn request failed: ${e?.message}`, view: 'QuoteCopilot', metadata: { dealId } });
    return { success: false, error: e?.message || 'network_error' };
  }
  // A refusal BEFORE the stream (auth, the 20/hour chat rate limit, kill switch) is JSON.
  if (!res.ok || !String(res.headers.get('content-type') || '').includes('text/event-stream')) {
    const body = await res.json().catch(() => ({ /* spine-emit-ok: non-JSON refusal body; its status is logged just below */ }));
    if (!res.ok) logAppError({ type: 'quote_copilot_stream_error', message: `turn refused: ${res.status}`, view: 'QuoteCopilot', metadata: { status: res.status, dealId } });
    return res.ok ? { success: true, ...body } : { success: false, status: res.status, error: body.error || `API error ${res.status}` };
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let final = null;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { frames, rest } = parseSseFrames(buffer);
      buffer = rest;
      for (const f of frames) {
        if (f.event === 'progress') { try { onProgress?.(f.data); } catch { /* spine-emit-ok: a UI progress callback must not break a paid turn */ } }
        else if (f.event === 'done') final = { success: true, ...f.data };
        else if (f.event === 'error') final = { success: false, status: f.data.status, error: f.data.error, code: f.data.code };
      }
    }
  } catch (e) {
    if (e?.name === 'AbortError') return { success: false, aborted: true }; // spine-emit-ok: the user pressed Stop
    logAppError({ type: 'quote_copilot_stream_error', message: `stream read failed: ${e?.message}`, view: 'QuoteCopilot', metadata: { dealId } });
    final = final || { success: false, error: e?.message || 'stream_error' };
  }
  if (!final) {
    logAppError({ type: 'quote_copilot_stream_error', message: 'stream ended without done/error', view: 'QuoteCopilot', metadata: { dealId } });
    return { success: false, error: 'The copilot stopped responding. What it wrote is saved; reload the thread.' };
  }
  if (!final.success) logAppError({ type: 'quote_copilot_stream_error', message: `turn failed: ${final.error}`, view: 'QuoteCopilot', metadata: { status: final.status || null, code: final.code || null, dealId } });
  return final;
}

/** Restore the quote to the snapshot taken before `turnId` wrote anything. */
export function revertCopilotTurn(turnId) {
  return apiFetch(AI, {
    method: 'POST',
    body: JSON.stringify({ action: 'agent.chat', scope: 'quote_copilot', op: 'revert', turn_id: turnId }),
    timeout: 30000,
    noRetry: true,
  });
}
